import { memo, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState, useTransition } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import { Icon } from '../components/Icon'
import { Empty } from '../components/misc'
import { JourneyListFilters } from '../components/JourneyListFilters'
import { JourneyListPrintButton } from './JourneyListPrint'
import { EMPTY_JOURNEY_FILTERS, EMPTY_JOURNEY_LIST_STATE, matchesJourneyTicketFilters, journeyDays, journeyListRequest } from '../features/journeyListFilters'
import type { JourneyListViewState } from '../features/journeyListFilters'
import { scheduleTime, scheduleDate, scheduleInstant } from '../domain/scheduling'
import { RowSkeleton } from '../components/Skeleton'
import { AggStatusBadge, PriorityFlag } from '../components/badges'
import { useTerms } from '../i18n/TermsContext'
import { useReference } from '../data/ReferenceContext'
import { counters, isActiveJourney, journeyDisplayStatus, isInProgress } from '../domain/engine'
import { isJunkCustomerName } from '../data/adapter/journey.adapter'
import type { Aggregator, AggStatus } from '../domain/types'
import type { JourneyFilter, JourneyPage, JourneyListCounters, JourneyCalendarLoader } from '../data/DataSource'
import type { Attendant } from '../domain/types'
import type { AppActions } from '../store/types'

export interface OnOpen { create: () => void; detalhes: (id: string) => void }

const SITUACAO_OPTIONS: { value: AggStatus; label: string }[] = [
  { value: 'Aguardando', label: 'Aguardando' },
  { value: 'Agendada', label: 'Agendada' },
  { value: 'Confirmada', label: 'Confirmada' },
  { value: 'AguardandoChamada', label: 'Aguardando chamada' },
  { value: 'Chamado', label: 'Chamado' },
  { value: 'EmAtendimento', label: 'Em atendimento' },
  { value: 'Completo', label: 'Completo' },
  { value: 'ParcialmenteCompleto', label: 'Parcialmente completo' },
  { value: 'Cancelado', label: 'Cancelado' },
]
// Sem cliente real: id 0 (anônimo), nome lixo ou identidade técnica de terminal.
const isAutoatendimento = (a: Aggregator): boolean =>
  a.cliente.id === 0
  || a.cliente.email.trim().toLowerCase().endsWith('@terminal.filazero.net')
  || isJunkCustomerName(a.cliente.nome)
const PAGE_SIZE = 15
const SITUACAO_PRIORITY: Record<AggStatus, number> = {
  Chamado: 0,
  AguardandoChamada: 1,
  EmAtendimento: 2,
  Confirmada: 3,
  Agendada: 4,
  Aguardando: 5,
  ParcialmenteCompleto: 6,
  Completo: 7,
  Cancelado: 8,
}

interface JourneyWorkRowProps {
  a: Aggregator
  aptos: (a: Aggregator) => number
  isSelected: boolean
  toggle: (id: string) => void
  actions: AppActions
  onOpen: OnOpen
  presetLabel: (key: string) => string
}

const JourneyWorkRow = memo(function JourneyWorkRow({ a, aptos, isSelected, toggle, actions, onOpen, presetLabel }: JourneyWorkRowProps) {
  const n = aptos(a)
  return (
    <div className="fz-elig" data-screen-label={`Agendada ${a.aggregatorId}`}>
      <input type="checkbox" className="fz-check" checked={isSelected} onChange={() => toggle(a.aggregatorId)} aria-label={`Selecionar ${a.cliente.nome}`} />
      <div className="fz-elig__body">
        <div className="fz-elig__name">
          {a.cliente.nome}
          <PriorityFlag prioridade={a.prioridade} />
        </div>
        <div className="fz-elig__meta">
          {a.metadata.templateKey && <span>{presetLabel(a.metadata.templateKey)}</span>}
          <span>{n} {n > 1 ? 'serviços' : 'serviço'} na jornada</span>
        </div>
      </div>
      <AggStatusBadge tickets={a.tickets} journeyStatus={a.status} sm />
      <button className="fz-btn fz-btn--ghost fz-btn--sm" disabled={actions.isConfirmingJourney(a.aggregatorId)} onClick={() => actions.confirmJourney(a.aggregatorId)}>
        <Icon name="check" size={14} /> {actions.isConfirmingJourney(a.aggregatorId) ? 'Confirmando…' : 'Confirmar jornada'}
      </button>
      <button className="fz-iconbtn" onClick={() => onOpen.detalhes(a.aggregatorId)} aria-label="Abrir atendimento"><Icon name="chevR" size={18} /></button>
    </div>
  )
})

interface ServerList {
  result: { key: string; page: JourneyPage } | null
  loading: boolean
  error: boolean
  counters: JourneyListCounters | null
  onQueryChange: (filter: JourneyFilter) => void
  onRetry: () => void
}

export function ListaScreen({ aggregators, aggsLoaded = false, search, onOpen, flashId, actions, locationName, dateFilter = 'today', onDateFilterChange, server, attendants: allAttendants = [], loadCalendar, listState }: {
  aggregators: Aggregator[]
  server?: ServerList
  attendants?: Attendant[]
  loadCalendar?: JourneyCalendarLoader
  listState?: { value: JourneyListViewState; onChange: Dispatch<SetStateAction<JourneyListViewState>> }
  /** true assim que o primeiro fetch de listJourneys completou (mesmo que retorne []). Sem
   *  isso, firstLoad nunca baixa quando a lista está vazia e o shimmer roda infinitamente. */
  aggsLoaded?: boolean
  search: string; onOpen: OnOpen; flashId: string | null; actions: AppActions; locationName: string; dateFilter?: 'today' | 'all'; onDateFilterChange?: (filter: 'today' | 'all') => void
}) {
  const [localListState, setLocalListState] = useState(EMPTY_JOURNEY_LIST_STATE)
  const currentListState = listState?.value ?? localListState
  const setListState = listState?.onChange ?? setLocalListState
  const filter = currentListState.group
  const filters = currentListState.filters
  const setFilter = (group: string) => setListState((previous) => ({ ...previous, group }))
  const setFilters = (next: SetStateAction<JourneyListViewState['filters']>) => setListState((previous) => ({
    ...previous, filters: typeof next === 'function' ? next(previous.filters) : next,
  }))
  const searchActive = search.trim() !== ''
  const searchWasActive = useRef(false)
  const filterBeforeSearch = useRef(filter)
  const hideAuto = filters.hideAuto
  const [remoteSearch, setRemoteSearch] = useState(search)
  useEffect(() => {
    const timer = setTimeout(() => setRemoteSearch(search), 250)
    return () => clearTimeout(timer)
  }, [search])
  const criteria = useMemo(() => journeyListRequest(filters, filter, remoteSearch, dateFilter), [filters, filter, remoteSearch, dateFilter])
  const criteriaKey = JSON.stringify(criteria)
  const [cursorHistory, setCursorHistory] = useState<{ key: string; cursors: (string | undefined)[]; index: number }>({ key: '', cursors: [undefined], index: 0 })
  const cursors = cursorHistory.key === criteriaKey ? cursorHistory.cursors : [undefined]
  const cursorIndex = cursorHistory.key === criteriaKey ? cursorHistory.index : 0
  const request = useMemo(() => ({ ...criteria, cursor: cursors[cursorIndex] }), [criteriaKey, cursors[cursorIndex]])
  const requestKey = JSON.stringify(request)
  const changeQuery = server?.onQueryChange
  useEffect(() => {
    if (remoteSearch.trim() !== search.trim()) {return}
    changeQuery?.(request)
  }, [changeQuery, requestKey, remoteSearch, search])
  const matchingPage = server?.result?.key === requestKey ? server.result.page : null
  const waitingForPage = !!server && !matchingPage && !server.error
  const showSkeleton = server ? waitingForPage : !aggsLoaded && aggregators.length === 0
  const busy = !!server && (server.loading || waitingForPage || remoteSearch.trim() !== search.trim())
  const navigatePage = (direction: -1 | 1) => {
    if (direction === 1) {
      const next = matchingPage?.nextCursor
      if (!next) {return}
      setCursorHistory({ key: criteriaKey, cursors: [...cursors.slice(0, cursorIndex + 1), next], index: cursorIndex + 1 })
    } else {setCursorHistory({ key: criteriaKey, cursors, index: Math.max(0, cursorIndex - 1) })}
  }
  const [page, setPage] = useState(1)
  // B-1: busca com prioridade transição — a digitação segue responsiva; o filtro re-roda
  // depois do commit do input. Fallback: useDeferredValue é no-op em perdas mínimas.
  const deferredSearch = useDeferredValue(search)
  useEffect(() => {
    if (searchActive && !searchWasActive.current) {
      setFilter('todos')
    } else if (!searchActive && searchWasActive.current) {
      setFilter(filterBeforeSearch.current)
    }
    searchWasActive.current = searchActive
  }, [searchActive])
  // B-2: mudanças de filtro/página renderizadas em baixa prioridade.
  const [, startFilterTransition] = useTransition()
  const [selected, setSelected] = useState<Set<string>>(new Set())
  // troca de aba/filtro zera a selecao - sem isso o botao bulk contava itens que sairam da tela
  useEffect(() => { setSelected(new Set()) }, [filter, filters, dateFilter, deferredSearch])
  useEffect(() => { setPage(1) }, [filter, filters, dateFilter, deferredSearch]) // B-1: filtro novo volta pra página 1
  const toggle = useCallback((id: string) => setSelected((prev) => {
    const n = new Set(prev)
    if (n.has(id)) {n.delete(id)} else {n.add(id)}
    return n
  }), [])
  // A-4: identidade estável para os props do JourneyWorkRow memoizado (onOpen chega
  // do App como objeto literal — nova identidade por render; preservamos o valor via ref).
  const onOpenRef = useRef(onOpen)
  onOpenRef.current = onOpen
  const stableOnOpen = useMemo(() => ({
    create: () => onOpenRef.current.create(),
    detalhes: (id: string) => onOpenRef.current.detalhes(id),
  }), [])
  const terms = useTerms()
  const { presetLabel, services, serviceConfigs, sessions: referenceSessions } = useReference()
  const calendarDays = useMemo(() => loadCalendar ? [] : journeyDays(aggregators), [aggregators, loadCalendar])
  const filterOptions = useMemo(() => {
    const sessionById = new Map(referenceSessions.map((session) => [session.sessionId, session]))
    const serviceOptions = new Map(services.map((service) => [String(service.serviceId), service.nome]))
    const attendants = new Map(allAttendants.map((attendant) => [attendant.id, attendant.nome]))
    const sessions = new Map(referenceSessions.map((session) => [String(session.sessionId), session.label]))
    for (const journey of aggregators) {
      for (const ticket of journey.tickets) {
        if (ticket.serviceId && ticket.nomeServico) {serviceOptions.set(String(ticket.serviceId), ticket.nomeServico)}
        if (ticket.resourceId) {attendants.set(String(ticket.resourceId), ticket.resourceNome || `Atendente ${ticket.resourceId}`)}
        if (ticket.sessionId != null) {
          const instant = ticket.sessionStart ? scheduleInstant(ticket.sessionStart) : NaN
          const day = Number.isFinite(instant) ? scheduleDate(new Date(instant)).split('-').reverse().join('/') : ''
          const time = Number.isFinite(instant) ? scheduleTime(ticket.sessionStart!) : ''
          sessions.set(String(ticket.sessionId), [ticket.nomeServico, day, time || sessionById.get(ticket.sessionId)?.label].filter(Boolean).join(' · '))
        }
      }
    }
    const options = (items: Map<string, string>) => [...items].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'))
    for (const id of filters.services) {if (!serviceOptions.has(id)) {serviceOptions.set(id, 'Serviço selecionado')}}
    for (const id of filters.attendants) {if (!attendants.has(id)) {attendants.set(id, 'Atendente selecionado')}}
    for (const id of filters.sessions) {if (!sessions.has(id)) {sessions.set(id, 'Sessão selecionada')}}
    return { services: options(serviceOptions), attendants: options(attendants), sessions: options(sessions) }
  }, [aggregators, services, referenceSessions, allAttendants, filters.services, filters.attendants, filters.sessions])

  // Deriva `_status` (memoizado) e os counts por filtro. Sem useMemo, cada keystroke na
  // busca re-roda `aggregators.map(... _status)` + 5 filters (counts) = O(N*5) por tecla.
  // Em 500 atendimentos = ~2.5k iterações + 1 sort O(N log N) por keystroke.
  const withStatus = useMemo(
    () => {
      const current = new Map(aggregators.map((journey) => [journey.aggregatorId, journey]))
      const items = server ? matchingPage?.items.map((item) => current.get(item.aggregatorId) ?? item) ?? [] : aggregators
      return items.map((a) => ({ ...a, _status: journeyDisplayStatus(a.status, a.tickets) }))
    },
    [aggregators, matchingPage, !!server],
  )
  const matchSearch = (a: Aggregator) => {
    if (!deferredSearch.trim()) {return true}
    const q = deferredSearch.toLowerCase()
    const cpf = a.cliente.cpf.startsWith('—') ? '' : a.cliente.cpf // placeholder '—' casa todo mundo (audit lista #10)
    return (a.cliente.nome.toLowerCase().includes(q) || (cpf && cpf.includes(q)) ||
      a.cliente.telefone.includes(q) || a.aggregatorId.toLowerCase().includes(q) || a.friendlyCode?.toLowerCase().includes(q) || a.tickets.some((ticket) => ticket.codigoTicket.toLowerCase().includes(q)) ||
      Object.values(a.metadata.fields).join(' ').toLowerCase().includes(q))
  }

  // Atendimentos permite apenas confirmar jornadas Waiting; as operações de ticket ficam no operador.
  const FILTERS = [
    { id: 'ativos', label: 'Ativos', test: (_s: string, a: Aggregator) => isActiveJourney(a.status, a.tickets) },
    { id: 'todos', label: 'Todos', test: () => true },
    { id: 'cancelados', label: 'Cancelados', test: (s: string) => s === 'Cancelado' },
  ]
  const searched = useMemo(() => withStatus.filter(matchSearch).filter((journey) => matchesJourneyTicketFilters(journey, filters)), [withStatus, deferredSearch, filters])
  const localCounts = useMemo(
    () => Object.fromEntries(FILTERS.map((f) => [f.id, searched.filter((a) => f.test(a._status, a)).length])),
    [searched],
  )
  const backendCounts = server?.counters
  const counts: Record<string, number | undefined> = server ? {
    ativos: backendCounts ? backendCounts.scheduled + backendCounts.serving : undefined,
    todos: backendCounts?.total,
    cancelados: backendCounts?.canceled,
  } : localCounts
  const active = FILTERS.find((f) => f.id === filter)!
  // Filtro + sort memoizados. Sort fica estável porque depende apenas de `_status` (memo de withStatus)
  // e `_createdAtMs` (já presente em Aggregator). Re-roda só quando o array de entrada ou algum
  // filtro do usuário muda — não a cada keystroke (search já está na dep, mas é barato).
  const localRows = useMemo(
    () => searched
      .filter((a) => active.test(a._status, a))
      .filter((a) => !filters.statuses.length || filters.statuses.includes(a._status))
      .filter((a) => !hideAuto || !isAutoatendimento(a))
      .sort((x, y) => SITUACAO_PRIORITY[x._status] - SITUACAO_PRIORITY[y._status] || (x._createdAtMs ?? 0) - (y._createdAtMs ?? 0)),
    [searched, active, filters, hideAuto],
  )

  const rows = server ? matchingPage ? withStatus : [] : localRows
  const totalPages = Math.max(1, Math.ceil((server ? matchingPage?.counters.total ?? 0 : rows.length) / PAGE_SIZE))
  const currentPage = server ? cursorIndex + 1 : Math.min(page, totalPages)
  const pageRows = server ? rows : rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)

  // ação em lote da aba pré-chamada corrente (null nas demais abas)
  const bulk = filters.statuses.length === 1 && filters.statuses[0] === 'Agendada'
  const aptos = useCallback((a: Aggregator) => a.tickets.filter((t) => t.status === 'Opened' || t.status === 'Authorized').length, [])
  const confirmingBulk = actions.isBulkActionPending?.('confirmJourneys') ?? false
  const printFilters = [
    filters.from || filters.to ? `${filters.from ? filters.from.split('-').reverse().join('/') : 'Início'} até ${filters.to ? filters.to.split('-').reverse().join('/') : 'Sem limite'}` : dateFilter === 'today' ? 'Hoje' : 'Todos os dias',
    filters.statuses.length ? SITUACAO_OPTIONS.filter((option) => filters.statuses.includes(option.value)).map((option) => option.label).join(', ') : active.label,
    ...(['services', 'attendants', 'sessions'] as const).map((key) => filterOptions[key].filter((option) => filters[key].includes(option.value)).map((option) => option.label).join(', ')),
    hideAuto ? 'Sem autoatendimentos' : '', searchActive ? `Busca: ${search.trim()}` : '',
  ].filter(Boolean)

  return (
    <div className="fz-page" data-screen-label="04 Lista de atendimentos">
      <div className="fz-page__head">
        <div>
          <h1 className="fz-page__title">{locationName}</h1>
          <div className="fz-page__sub fz-list__subtitle">
            <span>{searchActive ? `Busca ${filter === 'todos' ? 'em todas as situações' : `em ${active.label.toLowerCase()}`} · ${dateFilter === 'all' ? 'Todos os dias' : 'Hoje'}` : `Atendimentos · ${dateFilter === 'today' ? 'Visão do dia' : 'Todos os dias'}`}</span>
            <span className="fz-list__refresh" role="status">
              {busy && !showSkeleton && <><Icon name="refresh" size={12} className="fz-list__refresh-icon" /> Atualizando…</>}
            </span>
          </div>
        </div>
        <div className="fz-page__headbtns">
          <JourneyListPrintButton items={pageRows} locationName={locationName} filters={printFilters} page={currentPage} totalPages={totalPages}
            queueNames={Object.fromEntries(serviceConfigs.map((queue) => [queue.serviceConfigId, queue.nome]))}
            disabled={showSkeleton || busy || !!server?.error || deferredSearch !== search} />
          <button className="fz-btn fz-btn--primary" onClick={() => onOpen.create()}><Icon name="plus" size={16} style={{ filter: 'brightness(0) invert(1)' }} /> Novo atendimento</button>
        </div>
      </div>

      <JourneyListFilters quickFilters={<>
        {FILTERS.map((f) => (
          <button key={f.id} className={'fz-chip ' + (filter === f.id ? 'is-active' : '')} onClick={() => {
            // Grava a escolha do usuário de forma síncrona (fora da transição):
            // o effect de busca lê este ref, e o setFilter transicionado pode
            // não ter commitado quando a busca começar — capturar `filter` ali
            // gravaria o valor pré-clique e restauraria a aba errada ao limpar.
            filterBeforeSearch.current = f.id
            setFilters((previous) => ({ ...previous, statuses: [] }))
            startFilterTransition(() => setFilter(f.id))
          }}>
            {f.label} <span className="fz-chip__count">{counts[f.id] ?? (filter === f.id ? matchingPage?.counters.total : undefined) ?? '—'}</span>
          </button>
        ))}
      </>} value={filters} onChange={(next) => {
        const periodChanged = (next.from !== filters.from || next.to !== filters.to) && !!(next.from || next.to)
        const showAll = periodChanged || (next.statuses.length > 0 && next.statuses !== filters.statuses)
        if (showAll) {filterBeforeSearch.current = 'todos'}
        setListState((previous) => ({ ...previous, filters: next, group: showAll ? 'todos' : previous.group }))
      }} onClear={() => {
        setFilters(EMPTY_JOURNEY_FILTERS)
        filterBeforeSearch.current = 'ativos'
        setFilter(searchActive ? 'todos' : 'ativos')
        onDateFilterChange?.('today')
      }} statuses={SITUACAO_OPTIONS} calendarDays={calendarDays} loadCalendar={loadCalendar} {...filterOptions} dateFilter={dateFilter} onDateFilterChange={onDateFilterChange} />

      {server?.error && <div role="alert" className="fz-card fz-card--pad">Não foi possível carregar os atendimentos. <button className="fz-btn fz-btn--ghost fz-btn--sm" onClick={server.onRetry}>Tentar novamente</button></div>}
      {bulk ? (
        <>
          {rows.length > 0 && (
            <div className="fz-selbar">
              <label className="fz-selbar__all">
                <input type="checkbox" className="fz-check"
                  checked={pageRows.length > 0 && pageRows.every((a) => selected.has(a.aggregatorId))}
                  onChange={(e) => setSelected(e.target.checked ? new Set(pageRows.map((a) => a.aggregatorId)) : new Set())} />
                Selecionar todas
              </label>
              <span className="fz-selbar__count">{selected.size} selecionada{selected.size === 1 ? '' : 's'}</span>
              <span style={{ flex: 1 }} />
              <button className="fz-btn fz-btn--ghost fz-btn--sm" disabled={selected.size === 0 || confirmingBulk} aria-busy={confirmingBulk}
                onClick={() => actions.confirmJourneys([...selected], () => setSelected(new Set()))}>
                {confirmingBulk ? <span className="fz-spin" /> : <Icon name="check" size={14} />} {confirmingBulk ? 'Confirmando jornadas…' : `Confirmar ${selected.size > 0 ? `${selected.size} jornada${selected.size > 1 ? 's' : ''}` : 'selecionadas'}`}
              </button>
            </div>
          )}
          <div className="fz-rows">
            {showSkeleton && <RowSkeleton count={6} ariaLabel="Carregando atendimentos" />}
            {!showSkeleton && !server?.error && rows.length === 0 && <div className="fz-card fz-card--pad"><Empty title="Nenhuma jornada agendada" sub="Ninguém aguardando confirmação agora." /></div>}
            {!showSkeleton && !server?.error && pageRows.map((a) => <JourneyWorkRow key={a.aggregatorId} a={a} aptos={aptos} isSelected={selected.has(a.aggregatorId)} toggle={toggle} actions={actions} onOpen={stableOnOpen} presetLabel={presetLabel} />)}
          </div>
        </>
      ) : (
        <>
          <div className="fz-rows__header">
            <div>{terms.subject}</div>
            <div>Progresso</div><div>Situação</div><div></div>
          </div>
          <div className="fz-rows">
            {showSkeleton && <RowSkeleton count={6} ariaLabel="Carregando atendimentos" />}
            {!showSkeleton && !server?.error && rows.length === 0 && <div className="fz-card fz-card--pad"><Empty title="Nenhum atendimento aqui" sub="Ajuste o filtro ou a busca." /></div>}
            {!showSkeleton && !server?.error && pageRows.map((a) => {
              const c = counters(a.tickets)
              return (
                <button key={a.aggregatorId} className={'fz-row ' + (a.aggregatorId === flashId ? 'fz-flash' : '')} onClick={() => onOpen.detalhes(a.aggregatorId)}>
                  <div className="fz-row__patient">
                    <span className="fz-row__name">
                      {a.cliente.nome}
                      <PriorityFlag prioridade={a.prioridade} />
                    </span>
                    <span className="fz-row__id">{a.aggregatorId} · criado {a.criadoEm}</span>
                  </div>
                  <div className="fz-row__pips-cell">
                    <div className="fz-pips" style={{ marginBottom: 7 }}>
                      {a.tickets.map((t, i) => {
                        const tcls = t.status === 'Completed' ? 'is-done' : isInProgress(t.status) ? 'is-active' : t.status === 'Canceled' ? 'is-canceled' : ''
                        return <span key={`${t.ticketId}-${i}`} className={'fz-pip ' + tcls} title={t.nomeServico} />
                      })}
                    </div>
                    <span className="fz-prog__label" style={{ fontSize: 12 }}>{c.completos}/{c.total} concluídos</span>
                  </div>
                  <div><AggStatusBadge tickets={a.tickets} journeyStatus={a.status} sm /></div>
                  <div className="fz-row__chev"><Icon name="chevR" size={18} /></div>
                </button>
              )
            })}
          </div>
        </>
      )}

      {(totalPages > 1 || (server && cursorIndex > 0)) && (
        <div className="fz-pagination">
          <button className="fz-btn fz-btn--neutral fz-btn--sm" disabled={busy || currentPage === 1} onClick={() => server ? navigatePage(-1) : startFilterTransition(() => setPage(currentPage - 1))}>
            <Icon name="chevR" size={14} style={{ transform: 'rotate(180deg)' }} /> Anterior
          </button>
          <span className="fz-pagination__label">Página {currentPage} de {totalPages}</span>
          <button className="fz-btn fz-btn--neutral fz-btn--sm" disabled={busy || (server ? !matchingPage?.hasMore : currentPage === totalPages)} onClick={() => server ? navigatePage(1) : startFilterTransition(() => setPage(currentPage + 1))}>
            Próxima <Icon name="chevR" size={14} />
          </button>
        </div>
      )}
    </div>
  )
}
